import {Day} from "@progress/kendo-date-math"
import {
    Scheduler,
    SchedulerItem,
    SchedulerItemProps,
    SchedulerViewSlot,
    SchedulerViewSlotProps,
    WeekView,
    WorkWeekView,
} from "@progress/kendo-react-scheduler"
import {ButtonGroup, Button} from "@progress/kendo-react-buttons"
import {MDBCol, MDBRow} from "mdbreact"
import {useDispatch} from "react-redux"
import {fetchPackageSlots} from "../../containers/MotBooking/bookingSlice"
import {RootState} from "../../rootReducer"
import {SpinnerGrid} from "../../shared/CommonItems"
import {useAppSelector} from "../../store"
import {Dispatch, SetStateAction, useState} from "react"
import {SchedulerHeader, SchedulerHeaderProps} from "@progress/kendo-react-scheduler"
import dayjs from "dayjs"
import weekOfYear from "dayjs/plugin/weekOfYear"
import React from "react"

dayjs.extend(weekOfYear)

const modelFields: any = {
    start: "testStart",
    end: "testEnd",
}

const timeIntervals = [
    {
        name: "15 Mins",
        value: 15,
    },
    {
        name: "30 Mins",
        value: 30,
    },
    {
        name: "45 Mins",
        value: 45,
    },
    {
        name: "1 Hr",
        value: 60,
    },
]

const PackageSlotScheduler = ({
    dealerId,
    testPackageId,
    testDate,
    isSchedulerLoading,
    onTestSlotSelection,
}: {
    dealerId: number
    testPackageId: number
    testDate: Date
    isSchedulerLoading: boolean
    onTestSlotSelection: (start: Date, end: Date, selectedPackage: number, testFee: number, laneFee: number) => void
}) => {
    const dispatch = useDispatch()

    const regionId = useAppSelector((state: RootState) => state.application.selectedRegion.regionId)
    const packageSlots = useAppSelector((state: RootState) => state.containers.motBooking.packageSlots)
    const schedulesForMOT = useAppSelector((state: RootState) => state.application.schedulesForMOT)

    const [scheduleInterval, setIntervalValue] = useState<number>(60)

    const formatDateDigits = (date: number) => String(date).padStart(2, "0")

    const fetchLatestPackageSlot = (date: Date = testDate) => {
        dispatch(
            fetchPackageSlots(
                regionId,
                dealerId,
                testPackageId,
                `${formatDateDigits(date.getMonth() + 1)}/${formatDateDigits(date.getDate())}/${date.getFullYear()}`,
            ),
        )
    }

    const updateSelectedSlot = (start: Date, end: Date, selectedPackage: number, testFee: number, laneFee: number) => {
        onTestSlotSelection(start, end, selectedPackage, testFee, laneFee)
    }

    const setSlotItemClass = (dataItem: any) => {
        if (dataItem.slot === "Booked") {
            return "booked-color"
        } else if (dataItem.slot === "External") {
            return "external-color"
        } else if (dataItem.slot === "Break") {
            return "break-color"
        }
        return "available-color"
    }

    const CustomSchedulerItem = (props: SchedulerItemProps) => {
        const {start, end} = props
        const selectedPackage = props.dataItem.testPackageId
        const testFee = props.dataItem.testFee
        const laneFee = props.dataItem.laneFee
        const isSelectable = dayjs(start).toDate() >= dayjs().startOf("day").toDate() && props.dataItem.slot === ""
        return (
            <SchedulerItem
                {...props}
                onClick={() => {
                    updateSelectedSlot(start, end, selectedPackage, testFee, laneFee)
                }}
                className={`${!isSelectable ? "k-disabled" : ""} ${setSlotItemClass(props.dataItem)} `}
                style={{
                    ...props.style,
                    textAlign: "center",
                }}
            />
        )
    }

    const CustomViewSlot = (props: SchedulerViewSlotProps) => {
        return <SchedulerViewSlot {...props} expandable={false} style={{...props.style, padding: 0}} />
    }

    const IntervalBtnGroup = ({
        scheduleInterval,
        changeSchedulerInterval,
    }: {
        scheduleInterval: number
        changeSchedulerInterval: Dispatch<SetStateAction<number>>
    }) => {
        return (
            <ButtonGroup className="shadow-none float-end">
                {timeIntervals.map((item, index) => {
                    return (
                        <Button
                            className="btn-grey"
                            togglable={true}
                            onClick={() => changeSchedulerInterval(item.value)}
                            selected={item.value === scheduleInterval}
                            key={`plannerIntevalBtn_${index}`}
                            type="button"
                        >
                            {item.name}
                        </Button>
                    )
                })}
            </ButtonGroup>
        )
    }

    const SchedulerCustomHeader = (props: SchedulerHeaderProps) => {
        return (
            <SchedulerHeader>
                {props.children}
                <IntervalBtnGroup scheduleInterval={scheduleInterval} changeSchedulerInterval={setIntervalValue} />
            </SchedulerHeader>
        )
    }

    const getMinStartDateForWeek = () => {
        const selectedWeek = dayjs(testDate).week() - 1
        if (schedulesForMOT.length > 0) {
            const weekSchedules = schedulesForMOT.filter(x => dayjs(x.startDateTime).week() - 1 === selectedWeek)
            const startMin = weekSchedules.map(e => `${dayjs(e.startDateTime).format("HH:mm")}`).sort((a, b) => a.localeCompare(b))[0]
            return startMin
        }
        return "08:00"
    }

    const getMaxEndDateForWeek = () => {
        const selectedWeek = dayjs(testDate).week() - 1
        if (schedulesForMOT.length > 0) {
            const weekSchedules = schedulesForMOT.filter(x => dayjs(x.startDateTime).week() - 1 === selectedWeek)
            const endMax = weekSchedules
                .map(e => `${dayjs(e.endDateTime).format("HH:mm")}`)
                .sort((a, b) => a.localeCompare(b))
                .reverse()[0]
            return endMax
        }
        return "18:00"
    }

    return (
        <>
            <div>
                <div>
                    <MDBRow>
                        <MDBCol>
                            <div className="font-weight-bold text-muted">Bookings and available slots</div>
                        </MDBCol>
                        <MDBCol>
                            <div className="d-flex align-items-center justify-content-end">
                                <div className="box available-color me-2"></div>
                                <div className="me-4">Available Slot</div>
                                <div className="box booked-color k-disabled me-2"></div>
                                <div className="me-4">Booking</div>
                                <div className="box external-color k-disabled me-2"></div>
                                <div className="me-4">External Booking</div>
                                <div className="box break-color k-disabled me-2"></div>
                                <div className="me-4">Break</div>
                            </div>
                        </MDBCol>
                    </MDBRow>
                </div>
                <hr className="title-hr mt-0" />
                <div className="position-relative">
                    {isSchedulerLoading && <SpinnerGrid />}
                    <Scheduler
                        id="testBookingSlotScheduler"
                        // editable={{add: true, edit: false, remove: false, drag: false, resize: false, select: false}}
                        data={packageSlots}
                        defaultDate={testDate}
                        height={"75vh"}
                        modelFields={modelFields}
                        item={CustomSchedulerItem}
                        viewSlot={CustomViewSlot}
                        onDateChange={(props: {value: Date | undefined}) => fetchLatestPackageSlot(props.value)}
                        defaultView="week"
                        header={SchedulerCustomHeader}
                    >
                        <WorkWeekView
                            slotDuration={scheduleInterval}
                            slotDivisions={scheduleInterval === 15 || scheduleInterval === 45 ? 3 : 2}
                            showWorkHours={true}
                            title="Work Week"
                            workWeekStart={Day.Monday}
                            workWeekEnd={Day.Friday}
                            workDayStart={getMinStartDateForWeek()}
                            workDayEnd={getMaxEndDateForWeek()}
                        />
                        <WeekView
                            slotDuration={scheduleInterval}
                            slotDivisions={scheduleInterval === 15 || scheduleInterval === 45 ? 3 : 2}
                            showWorkHours={true}
                            title="Full Week"
                            workWeekStart={Day.Monday}
                            workWeekEnd={Day.Sunday}
                            workDayStart={getMinStartDateForWeek()}
                            workDayEnd={getMaxEndDateForWeek()}
                        />
                    </Scheduler>
                </div>
            </div>
        </>
    )
}

// export default PackageSlotScheduler

export default PackageSlotScheduler
